//Articles.tsx
import React from 'react'
import { axiosInstance, useAxios } from '../hooks/useAxios'
import { AxiosRequestConfig } from 'axios'
import { useLocation, useNavigate } from 'react-router'
import queryString from 'query-string'
import Tabs from './Tabs'
import Tab from './Tab'
import Skeleton from 'react-loading-skeleton'
import Select from './Select'
import { cloneDeep } from 'lodash-es'
import Pagination from './Pagination'
import Dialog from './Dialog'
import Btn from './Btn'
import { ReactSVG } from 'react-svg'
import BtnIcon from './BtnIcon'
import classNames from 'classnames'
import { useForm } from 'react-hook-form'
import SelectForForm from './SelectForForm'
import { useMediaQuery } from 'react-responsive'
import Card from './Card'
import Dropdown from './Dropdown'
import TextField from './TextField'

interface Props {
  method: (options: any) => AxiosRequestConfig
  queryMode?: boolean
  filtersHidden?: boolean
  noFilters?: boolean
  search?: boolean
  noSections?: boolean
  count?: number
  noPagination?: boolean
  contentGutterSmall?: boolean
  renderContent: (
    data: any,
    loading: boolean,
    updateSections: () => Promise<void>
  ) => React.ReactElement | string
  onLoad?: ({ response, error }: { response: any; error: any }) => void
  tabsNavigation?: boolean
  onSetup?: (refresh: any) => void
}

const Articles: React.FC<Props> = ({
  method,
  queryMode = false,
  filtersHidden = false,
  noFilters = false,
  search = false,
  noPagination = false,
  contentGutterSmall = false,
  count = 9,
  renderContent,
  noSections = false,
  onLoad,
  tabsNavigation,
  onSetup
}) => {
  const [responseData, setResponseData] = React.useState<any>({ items: [] })
  const [sections, setSections] = React.useState([])
  const [filters, setFilters] = React.useState([])
  const [pagination, setPagination] = React.useState({
    current: 1,
    total: 1
  })
  const contentRef = React.useRef<HTMLDivElement>(null)
  const isTablet = useMediaQuery({
    query: '(max-width: 1023px)'
  })

  const navigate = useNavigate()
  const location = useLocation()
  const [data, setData] = React.useState<any>(
    queryString.parse(location.search)
  )
  const { handleSubmit, control, setValue, reset } = useForm({
    defaultValues: Object.keys(data).reduce((acc: any, key) => {
      if (['page', 'section'].includes(key)) return acc
      acc[key] = Array.isArray(data[key]) ? data[key] : [data[key]]
      return acc
    }, {})
  })

  const prevData = React.useRef<any>(null)

  const { loading, fire, error } = useAxios()
  const { loading: loadingSection, fire: fireSection } = useAxios()
  const { loading: loadingFilters, fire: fireFilters } = useAxios()
  const { loading: loadingPagination, fire: firePagination } = useAxios()

  const headExists = React.useMemo(() => {
    return sections.length || filters.length || loading || loadingSection
  }, [sections, filters, loading, loadingSection])

  if (queryMode) {
    React.useEffect(() => {
      const parsed = queryString.parse(location.search)

      setData(parsed)
    }, [location.search])
  }

  if (search) {
    React.useEffect(() => {
      const parsed = queryString.parse(location.search)
      reset({
        searchValue: parsed.query || ''
      })
    }, [location.pathname, location.search])
  }

  React.useEffect(() => {
    const _prevData = cloneDeep(prevData.current ?? {})
    const _data = cloneDeep(data)

    delete _prevData.page
    delete _data.page
    delete _prevData.section
    delete _data.section

    if (!prevData.current) {
      const refresh = async (): Promise<any> => {
        const { response, error } = await fire(method({ ...data, count }))

        if (!error) {
          const _data = response?.data ?? {}

          setResponseData(_data.data)
          setSections(_data.sections ?? [])
          setFilters(_data.filters ?? [])
          setPagination(_data.pagination ?? { current: 1, total: 1 })
        }

        return { response, error }
      }

      onSetup?.(refresh)

      const handler = async () => {
        const res = await refresh()
        onLoad?.(res)
      }
      handler()
    } else if (data.section !== prevData.current.section) {
      filters.forEach((item: any) => {
        setValue(item.value, [])
      })

      const handler = async () => {
        const { response, error } = await fireSection(
          method({ ...data, count })
        )

        if (!error) {
          const _data = response?.data ?? {}

          setResponseData(_data.data)
          setFilters(_data.filters ?? [])
          setPagination(_data.pagination ?? { current: 1, total: 1 })
        }
      }

      handler()
    } else if (JSON.stringify(_prevData) !== JSON.stringify(_data)) {
      const keys = Object.keys(_data)

      if (keys.length > 0) {
        keys.forEach((key) => {
          const val = _data[key]

          setValue(key, Array.isArray(val) ? val : [val])
        })
      } else {
        filters.forEach((item: any) => {
          setValue(item.value, [])
        })
      }

      const handler = async () => {
        const { response, error } = await fireFilters(
          method({ ...data, count })
        )

        if (!error) {
          const _data = response?.data ?? {}

          setResponseData(_data.data)
          setPagination(_data.pagination ?? { current: 1, total: 1 })
        }
      }

      handler()
    } else if (prevData.current.page !== data.page) {
      const handler = async () => {
        const { response, error } = await firePagination(
          method({ ...data, count })
        )

        if (!error) {
          const _data = response?.data ?? {}

          setResponseData(_data.data)
        }
      }

      handler()
    }

    prevData.current = data
  }, [data])

  const onSelectChange = (value: any, name: string) => {
    if (!queryMode) {
      setData({ ...data, [name]: value })
    } else {
      const parsed = queryString.parse(location.search)

      parsed[name] = value
      delete parsed.page

      navigate(`?${queryString.stringify(parsed)}`)
    }
  }

  const handleSearch = async (query: { query: string }) => {
    const parsed = queryString.parse(location.search)
    parsed.query = query.query
    delete parsed.page
    navigate(`?${queryString.stringify(parsed)}`)
  }

  const responseDataIsLoading = React.useMemo(() => {
    return loading || loadingSection || loadingFilters || loadingPagination
  }, [loading, loadingSection, loadingFilters, loadingPagination])

  const updateSections = async (): Promise<void> => {
    try {
      const { data: _data } = await axiosInstance(method({ ...data, count: 1 }))

      if (_data?.data?.sections) {
        setSections(_data?.data?.sections)
      }
    } catch (_) {
      // Nothing
    }
  }

  const scrollToContent = () => {
    setTimeout(() => {
      contentRef?.current?.scrollIntoView({ behavior: 'smooth' })
    })
  }

  const onSubmit = (_data: any) => {
    if (!queryMode) {
      setData({ ...data, ..._data })
    } else {
      const parsed = queryString.parse(location.search)

      Object.keys(_data).forEach((key) => {
        parsed[key] = _data[key]
      })

      delete parsed.page

      navigate(`?${queryString.stringify(parsed)}`)
    }
  }

  const onReset = () => {
    const _data = filters.reduce((acc: any, item: any) => {
      acc[item.value] = []

      return acc
    }, {})

    onSubmit(_data)
  }

  const Form = (close: () => Promise<unknown>) => (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={'u-flex u-flexCol u-flexGrow1'}
      >
        <header className="card-head">
          <div className="card-head__title text-h3">Фильтр</div>
          <BtnIcon
            className={'card-head__control'}
            onClick={() => {
              close()
            }}
            filled
          >
            <ReactSVG src={'/i/svg/x.svg'} wrapper={'span'}></ReactSVG>
          </BtnIcon>
        </header>
        <div className={'card-content u-flexGrow1'}>
          {filters.map(({ label, value, options }, index) => {
            return (
              <SelectForForm
                control={control}
                className={classNames({
                  'mt-6': !!index
                })}
                dense
                hideDetails
                name={value}
                options={options}
                multiple
                key={value}
                label={label}
              ></SelectForForm>
            )
          })}
        </div>
        <div
          className={'card-foot pt-md-10 mt-sm-8 pb-sm-10'}
          style={{ marginTop: 'auto' }}
        >
          <Btn
            onClick={() => {
              close()
            }}
            style={{ width: '100%' }}
            type={'submit'}
          >
            Применить
          </Btn>
          <Btn
            onClick={() => {
              close()
              onReset()
            }}
            className={'mt-md-8 mt-sm-6'}
            style={{ width: '100%' }}
            text
          >
            Сбросить
          </Btn>
        </div>
      </form>
    </>
  )

  return loading || !error ? (
    <div className={'articles'}>
      {headExists ? (
        <header className="articles__head">
          {(loading && !noSections) ||
          sections.length ||
          (filtersHidden && filters.length) ? (
            <div className="articles__head-top">
              {loading && !noSections ? (
                <div className="articles__tabs row row-gap-20">
                  {Array.from({ length: isTablet ? 2 : 4 }).map((_, index) => (
                    <div style={{ width: '14rem' }} key={index}>
                      <Skeleton height={'4rem'}></Skeleton>
                    </div>
                  ))}
                </div>
              ) : sections.length ? (
                <Tabs
                  navigation={tabsNavigation}
                  className={'articles__tabs'}
                  initialIndex={sections.findIndex(
                    ({ value }) => value === data.section
                  )}
                  items={sections}
                  renderItem={(item, index) => {
                    return (
                      <Tab
                        alwaysShowCount={
                          !!sections.find(({ count }) => (count ?? 0) > 0)
                        }
                        count={item.count}
                        active={
                          (index === 0 && !data.section) ||
                          data.section === item.value
                        }
                        onClick={() => {
                          if (!queryMode) {
                            setData({ ...data, section: item.value })
                          }
                          if (search) {
                            reset({ searchValue: '' })
                          }
                        }}
                        to={
                          queryMode
                            ? !index
                              ? location.pathname
                              : `?section=${item.value}`
                            : undefined
                        }
                      >
                        {item.label}
                      </Tab>
                    )
                  }}
                ></Tabs>
              ) : null}
              {filtersHidden &&
              filters.length &&
              !loading &&
              !loadingSection ? (
                <Dropdown
                  style={{ display: isTablet ? 'none' : undefined }}
                  trigger={(open) => {
                    return (
                      <Btn
                        contrast
                        onClick={() => {
                          open()
                        }}
                      >
                        <span>Фильтр</span>
                        <ReactSVG
                          src={'/i/svg/filter.svg'}
                          wrapper={'span'}
                        ></ReactSVG>
                      </Btn>
                    )
                  }}
                  render={(close) => (
                    <Card style={{ width: '37.5rem' }} shadow>
                      {Form(close)}
                    </Card>
                  )}
                ></Dropdown>
              ) : null}
            </div>
          ) : null}
          {!noFilters ? (
            <>
              {loading || loadingSection ? (
                <div
                  className="articles__filters-wrapper"
                  style={{
                    display: filtersHidden && !isTablet ? 'none' : undefined
                  }}
                >
                  {!filtersHidden ? (
                    <div className="articles__filters row row-gap-20">
                      {Array.from({ length: isTablet ? 3 : 5 }).map(
                        (_, index) => (
                          <div style={{ width: '16rem' }} key={index}>
                            <Skeleton height={'4.2rem'}></Skeleton>
                          </div>
                        )
                      )}
                    </div>
                  ) : null}

                  <div className="articles__filters-dialog">
                    <Skeleton width={'100%'} height={'5rem'}></Skeleton>
                  </div>
                </div>
              ) : filters.length ? (
                <div
                  className="articles__filtres-wrapper"
                  style={{
                    display: filtersHidden && !isTablet ? 'none' : undefined
                  }}
                >
                  {!filtersHidden ? (
                    <div className="articles__filters row row-gap-20">
                      {filters.map(({ label, value, options }) => {
                        return (
                          <Select
                            dense
                            multiple
                            value={
                              (typeof data[value] === 'string'
                                ? [data[value]]
                                : data[value]) ?? []
                            }
                            key={value}
                            options={options}
                            label={label}
                            onChange={(val) => {
                              onSelectChange(val, value)
                            }}
                          ></Select>
                        )
                      })}
                    </div>
                  ) : null}

                  <div className="articles__filters-dialog">
                    <Dialog
                      filled
                      activator={(open) => (
                        <Btn
                          contrast
                          onClick={() => {
                            open()
                          }}
                        >
                          <span>Фильтр</span>
                          <ReactSVG
                            src={'/i/svg/filter.svg'}
                            wrapper={'span'}
                          ></ReactSVG>
                        </Btn>
                      )}
                      render={(close) => {
                        return Form(close)
                      }}
                    ></Dialog>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}

          {search && (
            <div className="articles__search">
              {loading || loadingSection ? (
                <Skeleton width={'100%'} height={'7rem'}></Skeleton>
              ) : (
                <form>
                  <TextField
                    control={control}
                    name="searchValue"
                    label="Поиск"
                    isSearch={true}
                    onSubmit={(query) => handleSearch(query)}
                  ></TextField>
                </form>
              )}
            </div>
          )}
        </header>
      ) : null}
      <div
        className={`articles__content ${
          contentGutterSmall ? '_gutter-sm' : ''
        }`}
        ref={contentRef}
      >
        {renderContent(responseData, responseDataIsLoading, updateSections)}
      </div>
      {!loading &&
      !loadingSection &&
      !loadingFilters &&
      !noPagination &&
      pagination.total > 1 ? (
        <div className="articles__pagination">
          <Pagination
            current={pagination.current}
            onChange={scrollToContent}
            total={pagination.total}
          ></Pagination>
        </div>
      ) : null}
    </div>
  ) : (
    error.message
  )
}

export default Articles
